import { OMSSubAccounts } from '@src/components/OMS/OMSSubAccounts';
import { useSubAccounts } from '@src/providers';
import { getRestFailureUserMessage } from '@src/utils/rtkQuery/restFailureError';
import {
  AllocationValueTypeEnum,
  AssetTransaction,
  Box,
  Button,
  ButtonVariants,
  Card,
  CopyButton,
  DateTimePicker,
  Divider,
  DrawerDivider,
  DrawerFooter,
  EllipsisBoxWithTooltip,
  FormControlSizes,
  FormGroup,
  FormMessage,
  HStack,
  Icon,
  IconName,
  InlineFormattedNumber,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  NotificationVariants,
  NumberInput,
  ProductTypeEnum,
  ScrollableBox,
  SearchSelect,
  Stack,
  Text,
  VStack,
  formattedDate,
  logger,
  productTypeLabel,
  useAssetsContext,
  useCurrenciesContext,
  useGlobalToasts,
  useJsonModal,
  useMarketAccountsContext,
  useMixpanel,
  useSecuritiesContext,
  type Allocation,
  type AssetTransactionTypeEnum as AssetTransactionTypeEnumType,
  type BoxProps,
} from '@talos/kyoko';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  isAllowedAssetTransactionProductType,
  prettifyAssetTransactionTypeEnum,
  requiresPositionAsset,
} from '../BalancesV2/assetTransactionTypeOptions';
import { useUpdateAssetTransactionMutation } from '../BalancesV2/assetTransactionsApi';
import { MAX_ASSET_TRANSACTION_COMMENT_LENGTH } from '../BalancesV2/useAdjustMarketAccountPositionsValidation';
import {
  buildUpdateAssetTransactionRequest,
  createModifyAssetTransactionForm,
  isConflictUpdateError,
  isModifyAssetTransactionFormDirty,
  validateModifyAssetTransactionForm,
  type ModifyAssetTransactionForm,
} from './modifyAssetTransactionForm';
import { TYPE_CHANGE_DESTINATION_OPTIONS, isAssetTransactionTypeEditable } from './modifyAssetTransactionTypes';

const CONFLICT_MESSAGE =
  'This transaction was updated elsewhere. Close and reopen Modify Transaction to load the latest revision.';

const METADATA_UPDATE_ERROR =
  'This transaction cannot be updated. Close and reopen after refreshing, or contact support if the problem continues.';

const NO_POSITION_ASSETS_WARNING = 'No Position Assets available on this Market Account';

function logModifyAssetTransactionMetadataError(transaction: AssetTransaction): void {
  logger.error(new Error('Modify asset transaction blocked: missing TID or Revision'), {
    transactionId: transaction.TransactionID,
    tid: transaction.TID,
    revision: transaction.Revision,
    type: transaction.Type,
    marketAccount: transaction.MarketAccount,
  });
}

const scrollOuterProps: BoxProps = { flex: 1, minHeight: 0, overflow: 'auto' };
const scrollInnerProps: BoxProps = { p: 'spacing12', overflow: 'auto' };

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

export function ModifyAssetTransactionDrawer({
  transaction,
  onClose,
  onSuccess,
}: {
  transaction: AssetTransaction;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [currentTransaction, setCurrentTransaction] = useState(transaction);
  const [form, setForm] = useState<ModifyAssetTransactionForm>(() => createModifyAssetTransactionForm(transaction));
  const [touched, setTouched] = useState<Partial<Record<keyof ModifyAssetTransactionForm, boolean>>>({});
  const [submitError, setSubmitError] = useState<string>();
  const [updateAssetTransaction, { isLoading, error, reset }] = useUpdateAssetTransactionMutation();
  const { add: addToast } = useGlobalToasts();
  const { handleClickJson, jsonModal } = useJsonModal<AssetTransaction>();
  const mixpanel = useMixpanel();

  const { assetsList, getAssetDisplaySymbol, resolveSecurityOrCurrency } = useAssetsContext();
  const { currenciesList } = useCurrenciesContext();
  const { securitiesList } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { allSubAccountBooks } = useSubAccounts();

  const typeEditable = isAssetTransactionTypeEditable(currentTransaction.Type);
  const errors = useMemo(
    () => validateModifyAssetTransactionForm(form, currentTransaction),
    [form, currentTransaction]
  );
  const isDirty = useMemo(
    () => isModifyAssetTransactionFormDirty(currentTransaction, form, allSubAccountBooks),
    [currentTransaction, form, allSubAccountBooks]
  );
  const isValid = isEmpty(errors);
  const getFieldError = useCallback(
    (field: keyof ModifyAssetTransactionForm) => {
      const error = errors[field];
      if (!error) {
        return undefined;
      }
      if (touched[field] || isDirty) {
        return error;
      }
      return undefined;
    },
    [errors, isDirty, touched]
  );
  const mutationErrorMessage = useMemo(() => {
    if (error == null) {
      return undefined;
    }
    const detail = getRestFailureUserMessage(error) ?? 'Something went wrong.';
    return isConflictUpdateError(detail) ? CONFLICT_MESSAGE : detail;
  }, [error]);
  const displayError = submitError ?? mutationErrorMessage;
  const positionAssetRequired = requiresPositionAsset(form.type);

  const updateForm = useCallback(
    (update: Partial<ModifyAssetTransactionForm>) => {
      setForm(prev => ({ ...prev, ...update }));
      setSubmitError(undefined);
      if (error != null) {
        reset();
      }
    },
    [error, reset]
  );

  const setAllTouched = useCallback(() => {
    setTouched({
      type: true,
      amount: true,
      asset: true,
      positionAsset: true,
      price: true,
      priceCurrency: true,
      rate: true,
      transactTime: true,
      comment: true,
      allocationValueType: true,
      subAccountAllocations: true,
      allocationCleared: true,
    });
  }, []);

  const assetOptions = useMemo(() => {
    return assetsList
      .filter(asset => {
        const { currency, security } = resolveSecurityOrCurrency(asset.Symbol);
        if (currency) {
          return true;
        }
        return security?.ProductType != null && isAllowedAssetTransactionProductType(security.ProductType);
      })
      .map(asset => asset.Symbol);
  }, [assetsList, resolveSecurityOrCurrency]);

  const selectablePositionAssetOptions = useMemo(() => {
    const marketAccount = marketAccountsByName.get(currentTransaction.MarketAccount);
    const market = currentTransaction.Market?.trim() || marketAccount?.Market;
    if (!market) {
      return [];
    }
    return securitiesList
      .filter(
        security => security.Markets?.includes(market) && isAllowedAssetTransactionProductType(security.ProductType)
      )
      .map(security => security.Symbol);
  }, [currentTransaction.Market, currentTransaction.MarketAccount, marketAccountsByName, securitiesList]);

  const positionAssetOptions = useMemo(() => {
    const current = form.positionAsset.trim();
    if (current && !selectablePositionAssetOptions.includes(current)) {
      return [...selectablePositionAssetOptions, current];
    }
    return selectablePositionAssetOptions;
  }, [form.positionAsset, selectablePositionAssetOptions]);
  const noSelectablePositionAssets = selectablePositionAssetOptions.length === 0;

  const currencyOptions = useMemo(() => currenciesList.map(currency => currency.Symbol), [currenciesList]);

  const getAssetGroup = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      if (currency) {
        return productTypeLabel(ProductTypeEnum.Spot);
      }
      if (security) {
        return productTypeLabel(security.ProductType);
      }
      return 'Unknown';
    },
    [resolveSecurityOrCurrency]
  );

  const getAssetDescription = useCallback(
    (asset: string) => {
      const { currency, security } = resolveSecurityOrCurrency(asset);
      return (currency ?? security)?.Description ?? '';
    },
    [resolveSecurityOrCurrency]
  );

  const marketAccountLabel =
    marketAccountsByName.get(currentTransaction.MarketAccount)?.DisplayName ?? currentTransaction.MarketAccount;
  const positionAsset = currentTransaction.PositionAsset?.trim() || undefined;
  const transactTimeDisplay = formattedDate(currentTransaction.TransactTime, '{yyyy}-{MM}-{dd} {HH}:{mm}:{ss}');
  const subAccountDisplay =
    currentTransaction.SubAccount ||
    currentTransaction.Allocation?.Allocations?.map(a => a.SubAccount).join(', ') ||
    '–';

  const handleShowJson = useCallback(() => {
    handleClickJson(currentTransaction);
  }, [handleClickJson, currentTransaction]);

  const handleClearAllocation = useCallback(() => {
    updateForm({
      allocationCleared: true,
      subAccountAllocations: [{ subAccount: '', value: '100' }],
      allocationValueType: AllocationValueTypeEnum.Percentage,
    });
    setTouched(prev => ({ ...prev, subAccountAllocations: true, allocationCleared: true }));
  }, [updateForm]);

  const handleSubAccountsUpdate = useCallback(
    (update: { allocationValueType: AllocationValueTypeEnum } | { subAccountAllocations: Allocation[] }) => {
      if ('subAccountAllocations' in update) {
        updateForm({
          subAccountAllocations: update.subAccountAllocations,
          allocationCleared: false,
        });
        setTouched(prev => ({ ...prev, subAccountAllocations: true }));
        return;
      }
      updateForm(update);
    },
    [updateForm]
  );

  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/monitoring')) {
      return;
    }
    onClose();
  }, [location.pathname, onClose]);

  const handleSubmit = useCallback(async () => {
    setAllTouched();
    setSubmitError(undefined);
    if (!isEmpty(errors)) {
      return;
    }

    // Revision 0 is valid for never-patched rows; only missing Revision/TID blocks the request.
    if (
      currentTransaction.TID == null ||
      String(currentTransaction.TID).trim() === '' ||
      currentTransaction.Revision == null
    ) {
      logModifyAssetTransactionMetadataError(currentTransaction);
      setSubmitError(METADATA_UPDATE_ERROR);
      return;
    }

    const request = buildUpdateAssetTransactionRequest(currentTransaction, form, allSubAccountBooks);
    if (request == null) {
      setSubmitError('No changes to save.');
      return;
    }

    try {
      const updatedTransactions = await updateAssetTransaction({
        tid: String(currentTransaction.TID),
        body: request,
      }).unwrap();
      const updatedTransaction = updatedTransactions[0];
      if (updatedTransaction?.TransactionID == null) {
        setSubmitError('Something went wrong.');
        return;
      }
      const next = new AssetTransaction(updatedTransaction);
      setCurrentTransaction(next);
      setForm(createModifyAssetTransactionForm(next));
      setTouched({});
      setSubmitError(undefined);
      mixpanel.track(MixpanelEvent.ModifyAssetTransaction, {
        [MixpanelEventProperty.Source]: MixpanelEventSource.MonitoringMarketCashEventsBlotter,
      });
      onSuccess();
    } catch (caughtError: unknown) {
      const detail = getRestFailureUserMessage(caughtError) ?? 'Something went wrong.';
      const message = isConflictUpdateError(detail) ? CONFLICT_MESSAGE : detail;
      setSubmitError(message);
      addToast({
        text: message,
        variant: NotificationVariants.Negative,
      });
    }
  }, [
    addToast,
    allSubAccountBooks,
    currentTransaction,
    errors,
    form,
    mixpanel,
    onSuccess,
    setAllTouched,
    updateAssetTransaction,
  ]);

  return (
    <ContentWrapper data-testid="modify-asset-transaction-drawer">
      <ScrollableBox outerProps={scrollOuterProps} innerProps={scrollInnerProps}>
        <Stack gap="spacing12" w="100%">
          <VStack gap="spacing8" w="100%" alignItems="stretch" data-testid="modify-asset-transaction-summary">
            <HStack w="100%" justifyContent="space-between" alignItems="flex-end" gap="spacing8">
              <Text fontSize="fontSizeSmall" color="colorTextSubtle">
                {transactTimeDisplay}
              </Text>
              <Button
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleShowJson}
                data-testid="modify-asset-transaction-show-json"
              >
                <HStack color="gray.090" gap="3px">
                  <Icon icon={IconName.Braces} />
                  Show JSON
                </HStack>
              </Button>
            </HStack>

            <Card display="flex" flexDirection="column" data-testid="modify-asset-transaction-summary-fields">
              <VStack gap="spacing8" w="100%" alignItems="stretch">
                <HStack justifyContent="space-between" gap="spacing16" alignItems="flex-start" w="100%" minWidth={0}>
                  <Text color="colorTextImportant" flexShrink={0}>
                    {prettifyAssetTransactionTypeEnum(currentTransaction.Type)}
                  </Text>
                  <CopyableId
                    label="TID"
                    value={String(currentTransaction.TID ?? '')}
                    copyAriaLabel="Copy TID"
                    copyTestId="modify-asset-transaction-copy-tid"
                  />
                </HStack>
                <MetadataRow label="External Transaction ID" value={String(currentTransaction.TransactionID ?? '')} />
                <MetadataRow
                  label="Amount"
                  value={
                    <MetadataValue>
                      <InlineFormattedNumber
                        number={currentTransaction.Amount}
                        currency={currentTransaction.Asset}
                        showDashIfEmpty
                        align="right"
                      />
                    </MetadataValue>
                  }
                />
                {positionAsset != null && <MetadataRow label="Position Asset" value={positionAsset} />}
                <Divider w="100%" />
                <MetadataRow label="Market" value={currentTransaction.Market || '–'} />
                <MetadataRow label="Market Account" value={marketAccountLabel || '–'} />
                <Divider w="100%" />
                <MetadataRow label="Sub Account" value={subAccountDisplay} />
                <MetadataRow label="Allocation Status" value={currentTransaction.AllocationStatus || '–'} />
              </VStack>
            </Card>
          </VStack>

          {jsonModal}

          <DrawerDivider />
          <Text color="colors.gray.100">General</Text>

          <FormGroup label="Type" error={getFieldError('type')}>
            <SearchSelect
              selection={form.type}
              options={TYPE_CHANGE_DESTINATION_OPTIONS}
              onChange={(value?: AssetTransactionTypeEnumType) => {
                if (value) {
                  updateForm({ type: value });
                  setTouched(prev => ({ ...prev, type: true }));
                }
              }}
              getLabel={prettifyAssetTransactionTypeEnum}
              disabled={!typeEditable || isLoading}
              data-testid="modify-asset-transaction-type"
            />
          </FormGroup>

          <HStack gap="spacing16" alignItems="flex-start" w="100%">
            <FormGroup label="Asset" required flex={1} error={getFieldError('asset')}>
              <SearchSelect
                selection={form.asset || undefined}
                options={assetOptions}
                onChange={value => {
                  updateForm({ asset: value ?? '' });
                  setTouched(prev => ({ ...prev, asset: true }));
                }}
                getGroup={getAssetGroup}
                showGroupTabs
                getLabel={getAssetDisplaySymbol}
                getDescription={getAssetDescription}
                data-testid="modify-asset-transaction-asset"
                maxHeight={400}
                disabled={isLoading}
              />
            </FormGroup>
            <FormGroup label="Amount" required flex={1} error={getFieldError('amount')}>
              <NumberInput
                value={form.amount}
                onChange={value => {
                  updateForm({ amount: value ?? '' });
                  setTouched(prev => ({ ...prev, amount: true }));
                }}
                data-testid="modify-asset-transaction-amount"
                disabled={isLoading}
              />
            </FormGroup>
          </HStack>

          <HStack gap="spacing16" alignItems="flex-start" w="100%">
            <FormGroup label="Price" flex={1} error={getFieldError('price')}>
              <NumberInput
                value={form.price}
                onChange={value => {
                  updateForm({ price: value ?? '' });
                  setTouched(prev => ({ ...prev, price: true }));
                }}
                data-testid="modify-asset-transaction-price"
                disabled={isLoading}
              />
            </FormGroup>
            <FormGroup label="Price Currency" flex={1} error={getFieldError('priceCurrency')}>
              <SearchSelect
                selection={form.priceCurrency || undefined}
                options={currencyOptions}
                onChange={value => {
                  updateForm({ priceCurrency: value ?? '' });
                  setTouched(prev => ({ ...prev, priceCurrency: true }));
                }}
                getLabel={value => value ?? ''}
                data-testid="modify-asset-transaction-price-currency"
                disabled={isLoading}
                showClear
              />
            </FormGroup>
          </HStack>

          <FormGroup label="Rate" error={getFieldError('rate')}>
            <NumberInput
              value={form.rate}
              onChange={value => {
                updateForm({ rate: value ?? '' });
                setTouched(prev => ({ ...prev, rate: true }));
              }}
              data-testid="modify-asset-transaction-rate"
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup
            label="Position Asset"
            required={positionAssetRequired}
            error={getFieldError('positionAsset')}
            disabled={isLoading || noSelectablePositionAssets}
            labelSuffix={
              noSelectablePositionAssets ? (
                <Text
                  color="colorTextSubtle"
                  fontSize="fontSizeSmall"
                  data-testid="modify-asset-transaction-no-position-assets"
                >
                  {NO_POSITION_ASSETS_WARNING}
                </Text>
              ) : undefined
            }
          >
            <SearchSelect
              selection={form.positionAsset || undefined}
              options={positionAssetOptions}
              onChange={value => {
                updateForm({ positionAsset: value ?? '' });
                setTouched(prev => ({ ...prev, positionAsset: true }));
              }}
              getGroup={getAssetGroup}
              showGroupTabs
              getLabel={getAssetDisplaySymbol}
              getDescription={getAssetDescription}
              data-testid="modify-asset-transaction-position-asset"
              maxHeight={400}
              disabled={isLoading || noSelectablePositionAssets}
              showClear={!positionAssetRequired}
            />
          </FormGroup>

          <FormGroup label="Transact Time" required error={getFieldError('transactTime')}>
            <DateTimePicker
              value={form.transactTime}
              onChange={value => {
                updateForm({ transactTime: value });
                setTouched(prev => ({ ...prev, transactTime: true }));
              }}
              data-testid="modify-asset-transaction-transact-time"
              disabled={isLoading}
            />
          </FormGroup>

          <FormGroup label="Comment" error={getFieldError('comment')}>
            <Input
              value={form.comment}
              onChange={e => {
                updateForm({ comment: e.target.value });
                setTouched(prev => ({ ...prev, comment: true }));
              }}
              data-testid="modify-asset-transaction-comment"
              disabled={isLoading}
              maxLength={MAX_ASSET_TRANSACTION_COMMENT_LENGTH}
            />
          </FormGroup>

          <DrawerDivider />
          <HStack justifyContent="space-between" alignItems="center" w="100%">
            <Text color="colors.gray.100">Allocation</Text>
            <Button
              size={FormControlSizes.Tiny}
              onClick={handleClearAllocation}
              disabled={isLoading}
              data-testid="modify-asset-transaction-clear-allocation"
            >
              Clear Allocation
            </Button>
          </HStack>

          <OMSSubAccounts
            subAccountAllocations={form.subAccountAllocations}
            allocationValueType={form.allocationValueType}
            onUpdate={handleSubAccountsUpdate}
            quantityCurrency={form.asset || undefined}
            useAllocations={true}
            errors={{
              subAccountAllocations: getFieldError('subAccountAllocations'),
            }}
            touched={{ subAccountAllocations: Boolean(touched.subAccountAllocations) }}
            updateDefaultSubAccountOnChange={false}
            disabled={isLoading}
          />
        </Stack>
      </ScrollableBox>

      <DrawerFooter data-testid="modify-asset-transaction-drawer-footer" w="100%">
        <Stack w="100%" gap="spacing8">
          {displayError && (
            <FormMessage error data-testid="modify-asset-transaction-submit-error">
              {displayError}
            </FormMessage>
          )}
          <HStack w="100%" gap="spacing16">
            <Button
              onClick={onClose}
              variant={ButtonVariants.Default}
              flex={1}
              disabled={isLoading}
              data-testid="modify-asset-transaction-close"
            >
              Close
            </Button>
            <Button
              variant={ButtonVariants.Primary}
              flex={1}
              onClick={handleSubmit}
              disabled={!isDirty || !isValid || isLoading}
              data-testid="modify-asset-transaction-update"
            >
              {isLoading ? 'Saving…' : 'Update'}
            </Button>
          </HStack>
        </Stack>
      </DrawerFooter>
    </ContentWrapper>
  );
}

function CopyableId({
  label,
  value,
  copyAriaLabel,
  copyTestId,
}: {
  label: string;
  value: string;
  copyAriaLabel?: string;
  copyTestId?: string;
}) {
  const copyButton =
    copyAriaLabel != null && copyTestId != null ? (
      <Box transform="translateY(-0.5px)" display="inline-flex">
        <CopyButton aria-label={copyAriaLabel} data-testid={copyTestId} size={FormControlSizes.Tiny} value={value} />
      </Box>
    ) : undefined;

  return (
    <HStack maxWidth="100%" minWidth={0} w="100%" justifyContent="flex-end" overflow="hidden">
      <EllipsisBoxWithTooltip
        prefixNode={copyButton}
        color="colorTextSubtle"
        fontSize="fontSizeDefault"
        outerBoxProps={{ minWidth: 0, maxWidth: '100%', flex: '0 1 auto' }}
      >
        {label}: {value}
      </EllipsisBoxWithTooltip>
    </HStack>
  );
}

function MetadataValue({ children }: { children: ReactNode }) {
  return (
    <Box color="colorTextImportant" fontSize="fontSizeMd" maxWidth="100%" overflowWrap="anywhere" textAlign="right">
      {children}
    </Box>
  );
}

function MetadataRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <HStack justifyContent="space-between" gap="spacing16" alignItems="flex-start" w="100%">
      <Text flexShrink={0} color="colorTextSubtle">
        {label}
      </Text>
      {typeof value === 'string' ? <MetadataValue>{value}</MetadataValue> : value}
    </HStack>
  );
}
